import type { CallEndReason } from "@dialstack/sdk-webrtc";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";

import type { IncomingCallPop } from "./incoming-call-pop.slice";

export type InboundCallStatusEvent = "answered" | "ended";

// One websocket `voice.inbound_call_status` frame (dialstack/notifications.py):
// an inbound call this user took, and how it is going. The backend sends it
// only to the user DialStack names as the answerer, so a browser that saw its
// own ringing leg end knows whether the call went to this user's handset or
// desk phone (keep the pop open, ENG-2562) or to somebody else (let it go).
export type InboundCallStatus = {
  event: InboundCallStatusEvent;
  // Provider call id (Call.dialstack_call_id) — the SDK's and the pop's id.
  callId: string;
  // Backend Call.id, for the phone note and the unknown-caller actions.
  backendCallId: number | null;
  clinicId: number | null;
  callStatus: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  receivedAt: number;
};

// How this browser's own leg of an inbound call ended (use-device-call.hook):
// the pop it was joined to, whether the call was answered HERE — in which
// case a status frame for it is this browser's own call, not a handset's —
// and, for a leg that was still ringing, the reason the SDK gave. A ringing
// leg the server hung up went somewhere else: this user's handset, a
// colleague, or the caller gave up.
export type BrowserLegEnd = {
  callId: string;
  pop: IncomingCallPop | null;
  backendCallId: number | null;
  answeredHere: boolean;
  endedRinging: boolean;
  endReason: CallEndReason | null;
  endedAt: number;
};

type DeviceCallState = {
  statusByCallId: Record<string, InboundCallStatus>;
  legsByCallId: Record<string, BrowserLegEnd>;
  // Calls whose "on your phone" card the user closed, or that aged out.
  dismissedCallIds: string[];
  // Calls the user declined on this computer: their ringing leg ending is
  // not a handset answer, so no "Connecting…" hold for them.
  declinedCallIds: string[];
};

const EVENT_ORDER: Record<InboundCallStatusEvent, number> = {
  answered: 0,
  ended: 1
};

// A call answered on the handset stays on the card this long without an
// "ended" frame: a lost call.end must not pin the pop up for the whole shift.
export const DEVICE_CALL_MAX_AGE_MS = 4 * 60 * 60_000;
// How long the card holds its place as "Connecting…" after the browser's
// ringing leg ends, waiting for the backend to say the call is on this user's
// handset (ENG-2605). The webhook is a second or two behind the pick-up; with
// no frame by then a colleague took the call and the card goes.
export const HANDSET_ANSWER_GRACE_MS = 10_000;
const MAX_ENTRIES = 10;

// The auth logout thunk's fulfilled action type; matched by string so this
// slice does not import the auth slice (which imports the API clients).
const LOGOUT_FULFILLED = "auth/logout/fulfilled";

const initialState: DeviceCallState = {
  statusByCallId: {},
  legsByCallId: {},
  dismissedCallIds: [],
  declinedCallIds: []
};

const remember = (ids: string[], id: string) =>
  ids.includes(id) ? ids : [id, ...ids].slice(0, MAX_ENTRIES);

function pruneOldest<T>(
  record: Record<string, T>,
  at: (entry: T) => number
): Record<string, T> {
  const entries = Object.entries(record);
  if (entries.length <= MAX_ENTRIES) return record;
  return Object.fromEntries(
    entries.sort((a, b) => at(b[1]) - at(a[1])).slice(0, MAX_ENTRIES)
  );
}

export const deviceCallSlice = createSlice({
  name: "deviceCall",
  initialState,
  reducers: {
    // Webhooks are delivered at least once and in no guaranteed order: a
    // re-sent `answered` must not revive a call that has ended.
    inboundCallStatusReceived: (
      state,
      action: PayloadAction<InboundCallStatus>
    ) => {
      const existing = state.statusByCallId[action.payload.callId];
      if (
        existing &&
        EVENT_ORDER[existing.event] > EVENT_ORDER[action.payload.event]
      )
        return;
      state.statusByCallId[action.payload.callId] = action.payload;
      state.statusByCallId = pruneOldest(
        state.statusByCallId,
        (status) => status.receivedAt
      );
    },
    browserLegEnded: (state, action: PayloadAction<BrowserLegEnd>) => {
      state.legsByCallId[action.payload.callId] = action.payload;
      state.legsByCallId = pruneOldest(
        state.legsByCallId,
        (leg) => leg.endedAt
      );
    },
    deviceCallDismissed: (state, action: PayloadAction<string>) => {
      state.dismissedCallIds = remember(state.dismissedCallIds, action.payload);
    },
    browserLegDeclined: (state, action: PayloadAction<string>) => {
      state.declinedCallIds = remember(state.declinedCallIds, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === LOGOUT_FULFILLED,
      () => initialState
    );
  }
});

export const {
  inboundCallStatusReceived,
  browserLegEnded,
  deviceCallDismissed,
  browserLegDeclined
} = deviceCallSlice.actions;

export const selectInboundCallStatuses = (state: RootState) =>
  state.deviceCall.statusByCallId;
export const selectBrowserLegEnds = (state: RootState) =>
  state.deviceCall.legsByCallId;
export const selectDismissedDeviceCallIds = (state: RootState) =>
  state.deviceCall.dismissedCallIds;
export const selectDeclinedCallIds = (state: RootState) =>
  state.deviceCall.declinedCallIds;

export default deviceCallSlice.reducer;
